/** @format */

import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { isNil } from 'lodash-es';
import { ModelMapper } from 'model-mapper';
import 'moment-timezone';
import { lastValueFrom, map } from 'rxjs';
import { EquipmentStatus } from '../classes/equipment';
import { EquipmentKind, EquipmentKindCategory } from '../classes/equipment-kind';
import { Family } from '../classes/family';
import { buildHeaderSearchFilter, IHeaderSearch } from '../classes/header-search';
import { Inspection } from '../classes/inspection';
import { LibConfig, LibConfigService } from '../config.service';

export interface IEquipmentFilter extends IHeaderSearch {
  organizationalUnitId?: string;
  statuses?: EquipmentStatus[];
  families?: Family[];
  categories?: EquipmentKindCategory[];
  contractorIds?: string[];
  equipmentKindIds?: string[];
  supervision?: boolean;
  hypervision?: boolean;
  realEstateStructureId?: string;
  equipmentId?: string;
  equipmentKindId?: string;
  equipmentKind?: EquipmentKind;
  signatureSettingName?: string;
  isTertiary?: boolean;
  supervised?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class EquipmentService {
  constructor(@Inject(LibConfigService) protected config: LibConfig, protected http: HttpClient) {}

  public async getSate(search: IEquipmentFilter): Promise<number> {
    return lastValueFrom(
      this.http.post<number>(`${this.config.environment.apiUrl}/equipments/get-state`, this.buildSearchFilter(search))
    );
  }

  public count(
    search: IEquipmentFilter
  ): Promise<{ family: string; category: string; kindId: string; count: number }[]> {
    const filter = this.buildSearchFilter(search);
    return lastValueFrom(this.http.post<any>(`${this.config.environment.apiUrl}/equipments/get-count`, filter));
  }

  public getInspections(id: string): Promise<Inspection[]> {
    return lastValueFrom(
      this.http
        .get<Inspection[]>(`${this.config.environment.apiUrl}/equipments/${id}/inspections`)
        .pipe(map(data => data.map(d => new ModelMapper(Inspection).map(d))))
    );
  }

  protected buildSearchFilter(search: IEquipmentFilter): any {
    const filter = buildHeaderSearchFilter(search);
    if (search?.organizationalUnitId) filter.organizationalUnitId = search.organizationalUnitId;
    if (search?.statuses) filter.statuses = search.statuses;
    if (search?.categories) filter.categories = search.categories;
    if (search?.families) filter.families = search.families;
    if (search?.contractorIds?.length) filter.contractorIds = search.contractorIds;
    if (search?.equipmentKindIds?.length) filter.equipmentKindIds = search.equipmentKindIds;
    if (!isNil(search?.supervision)) filter.supervision = search.supervision;
    if (!isNil(search?.hypervision)) filter.hypervision = search.hypervision;
    if (search?.realEstateStructureId) filter.realEstateStructureId = search.realEstateStructureId;
    if (search?.equipmentId) filter.equipmentId = search.equipmentId;
    if (search?.equipmentKindId) filter.equipmentKindId = search.equipmentKindId;
    if (search?.equipmentKind) filter.equipmentKindId = search.equipmentKind.id;
    if (search?.signatureSettingName) filter.signatureSettingName = search.signatureSettingName;
    if (!isNil(search?.isTertiary)) filter.isTertiary = search.isTertiary;
    if (!isNil(search?.supervised)) filter.supervised = search.supervised;
    if (search.reference) filter.reference = search.reference;
    return filter;
  }
}
